import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
} from "@material-ui/core";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const SIGNATURE_RULE_OPTIONS = [
  { value: "", label: "settingsExtra.firmaReglaNoneOption" },
  { value: "always", label: "settingsExtra.firmaReglaAlwaysOption" },
  { value: "never", label: "settingsExtra.firmaReglaNeverOption" },
  { value: "prefer_on", label: "settingsExtra.firmaReglaPreferOnOption" },
  { value: "prefer_off", label: "settingsExtra.firmaReglaPreferOffOption" },
];

const ContactTypeModal = ({ open, onClose, contactType, signatureFeatureEnabled }) => {
  const [name, setName] = useState("");
  const [color, setColor] = useState("#667eea");
  const [signatureRule, setSignatureRule] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (contactType) {
      setName(contactType.name || "");
      setColor(contactType.color || "#667eea");
      setSignatureRule(contactType.signatureRule || "");
    }
  }, [contactType, open]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error(i18n.t("contactTypeModal.errNameRequired"));
      return;
    }
    setSaving(true);
    try {
      await api.put(`/contact-types/${contactType.id}`, { name: name.trim(), color });

      if (signatureFeatureEnabled) {
        await api.put(`/contact-types/${contactType.id}/signature-rule`, {
          rule: signatureRule || null
        });
      }

      toast.success(i18n.t("contactTypeModal.savedOk"));
      onClose(true);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="sm">
      <DialogTitle>{i18n.t("contactTypeModal.title")}</DialogTitle>
      <DialogContent>
        <TextField
          label={i18n.t("contactTypeModal.name")}
          value={name}
          onChange={e => setName(e.target.value)}
          fullWidth
          margin="dense"
          variant="outlined"
          autoFocus
        />
        <Box mt={2} display="flex" alignItems="center" style={{ gap: 12 }}>
          <TextField
            label={i18n.t("contactTypeModal.color")}
            value={color}
            onChange={e => setColor(e.target.value)}
            margin="dense"
            variant="outlined"
            style={{ width: 140 }}
          />
          <Box
            width={32}
            height={32}
            borderRadius="50%"
            style={{ backgroundColor: color, border: "1px solid #ccc", flexShrink: 0 }}
          />
          <input
            type="color"
            value={color}
            onChange={e => setColor(e.target.value)}
            style={{ width: 40, height: 32, cursor: "pointer", border: "none" }}
          />
        </Box>

        {signatureFeatureEnabled && (
          <FormControl variant="outlined" margin="dense" fullWidth style={{ marginTop: 16 }}>
            <InputLabel>{i18n.t("contactTypeModal.signatureRule")}</InputLabel>
            <Select
              value={signatureRule}
              onChange={e => setSignatureRule(e.target.value)}
              label={i18n.t("contactTypeModal.signatureRule")}
            >
              {SIGNATURE_RULE_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {i18n.t(opt.label)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} disabled={saving}>
          {i18n.t("contactTypeModal.cancel")}
        </Button>
        <Button onClick={handleSave} color="primary" disabled={saving} variant="contained">
          {saving ? <CircularProgress size={20} /> : i18n.t("contactTypeModal.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactTypeModal;
